import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The guide's section header: an optional leading icon, an uppercase tracked
 * label, an optional count in brand blue, and a divider that fades out to
 * the right.
 *
 * Quoted from `elevated-polish-guide.md` > Section Headers. The contrast here
 * comes from WEIGHT, not from darkening the text — `font-black` at 11px with
 * 0.1em tracking reads far harder than the `text-xs font-medium text-slate-500`
 * these replace, while staying slate-500 as the guide specifies.
 */
export interface SectionHeaderProps {
  children: ReactNode;
  /** Leading icon — `<Icon className="w-3.5 h-3.5 text-slate-400" />` per the guide. */
  icon?: LucideIcon;
  /** Rendered after the label in brand blue — a row count, a total. */
  count?: number | string;
  /** Right-aligned controls: a "view all" link, a filter, a toggle. */
  actions?: ReactNode;
  className?: string;
}

export function SectionHeader({ children, icon: Icon, count, actions, className = '' }: SectionHeaderProps) {
  return (
    <div className={`mb-3 flex items-center gap-2 ${className}`.trim()}>
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      <h2 className="text-[11px] font-black uppercase tracking-[0.1em] text-slate-500">{children}</h2>
      {count !== undefined && (
        <span className="text-[10px] font-black tabular-nums text-[#007ACC]">&middot; {count}</span>
      )}
      <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent" />
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
